import { CheckIcon, FileIcon } from 'lucide-react';
import type { JSX } from 'react';

import {
  nameWithMark,
  RowMark,
  rowMarkClass,
} from '@/components/ui/row-mark.js';
import { cn } from '@/lib/utils.js';

/**
 * One file offered for picking, as a toggle. The whole row is the control — a checkbox beside it would be
 * a second control for the one thing the row does — and `aria-pressed` is what says whether the file is in
 * the batch. The box on the left is a drawn indicator, not a nested `<button>`: Radix's Checkbox renders
 * one, and a button inside a button is invalid HTML and breaks assistive tech.
 *
 * It lives in the shared kit rather than in a feature because both sources a message can attach a file
 * from render it — the workspace's artifacts and the workspace's SharePoint tree — and one row is what
 * keeps picking a file the same gesture wherever the file happens to live.
 */
export function FilePickerRow({
  name,
  meta,
  place,
  mark,
  selected,
  onToggle,
  className,
}: {
  name: string;
  /** The trailing detail, where the source has one to show (type and size, say). */
  meta?: string;
  /**
   * Where the file lives, for a list whose names repeat. It is a separate prop from {@link meta} because
   * of what it is FOR: a size describes a file, a place identifies it, so this one belongs in the row's
   * accessible name and a size does not. Left off where the surrounding list already says the place.
   */
  place?: string;
  /**
   * A state this file is IN — see {@link RowMark}. It rides alongside the pick rather than replacing it:
   * the pick owns the row's fill and `aria-pressed`, the mark owns the row's edge and the badge, so a file
   * that is both marked and picked says both things at once.
   */
  mark?: string;
  selected: boolean;
  onToggle: () => void;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      aria-label={nameWithMark(
        place ? `Select ${name} in ${place}` : `Select ${name}`,
        mark,
      )}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        rowMarkClass(mark !== undefined),
        selected ? 'bg-primary/10' : 'hover:bg-muted',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
          selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border',
        )}
      >
        {selected && <CheckIcon className="size-3" />}
      </span>
      <FileIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {name}
      </span>
      {mark !== undefined && <RowMark label={mark} />}
      {meta !== undefined && (
        <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>
      )}
      {place ? (
        <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">
          {place}
        </span>
      ) : null}
    </button>
  );
}
